import mipmapShader from '../shaders/mipmap-generator.wgsl';

export class MipMapGenerator {
  module: GPUShaderModule | null;
  pipelineByFormat: Partial<{ [key in GPUTextureFormat]: GPURenderPipeline }>;
  sampler: GPUSampler | null;
  private device: GPUDevice | null;

  constructor() {
    this.module = null;
    this.sampler = null;
    this.pipelineByFormat = {};
    this.device = null;
  }

  /**
   * Fills mip levels 1..n of `texture` by successively half-scaling the level
   * above, for the single array layer `baseArrayLayer` (0 for a plain 2D
   * texture). Array textures need one call per layer.
   *
   * Every view is created with an explicit `dimension: '2d'` and a single
   * layer: WebGPU defaults a layered texture's view to `2d-array`, which will
   * not bind to the `texture_2d<f32>` this generator's shader declares. That
   * default is why layered textures previously shipped without mips.
   *
   * Averaging happens in linear space for free, and only because the source
   * and destination views share the texture's own format: for an `-srgb`
   * texture the sampler decodes before the hardware blends the four texels,
   * and the colour attachment re-encodes on write. Nothing here needs to know
   * which space it is in — but a caller that reformatted either view (via
   * `viewFormats`) to strip the `-srgb` suffix would silently be back to
   * averaging the encoding.
   */
  generateMips(
    device: GPUDevice,
    texture: GPUTexture,
    baseArrayLayer = 0,
    timestamps?: GPURenderPassTimestampWrites
  ) {
    // The module, sampler and pipelines all belong to the device that created
    // them, so a new device invalidates every cached object.
    if (this.device !== device) {
      this.device = device;
      this.module = null;
      this.sampler = null;
      this.pipelineByFormat = {};
    }

    const pipelines = this.pipelineByFormat;

    if (!this.module) {
      this.module = device.createShaderModule({
        label: 'textured quad shaders for mip level generation',
        code: mipmapShader,
      });

      this.sampler = device.createSampler({
        minFilter: 'linear',
      });
    }

    if (!pipelines[texture.format]) {
      pipelines[texture.format] = device.createRenderPipeline({
        label: 'mip level generator pipeline',
        layout: 'auto',
        vertex: {
          module: this.module!,
          entryPoint: 'vs',
        },
        fragment: {
          module: this.module!,
          entryPoint: 'fs',
          targets: [{ format: texture.format }],
        },
      });
    }

    const pipeline = pipelines[texture.format]!;

    const encoder = device.createCommandEncoder({
      label: 'mip gen encoder',
    });

    let width = texture.width;
    let height = texture.height;
    let baseMipLevel = 0;

    // `timestamps` (GpuPassTimer) spans the chain: its start on the first
    // level's pass, its end on the last's.
    let levels = 0;
    for (
      let w = width, h = height;
      (w > 1 || h > 1) && levels + 1 < texture.mipLevelCount;
      levels++
    ) {
      w = Math.max(1, (w / 2) | 0);
      h = Math.max(1, (h / 2) | 0);
    }
    const span = timestamps as unknown as
      | {
          querySet: GPUQuerySet;
          beginningOfPassWriteIndex: number;
          endOfPassWriteIndex: number;
        }
      | undefined;

    // Stops at the chain the texture was created with, which may be shorter
    // than a full one.
    while (
      (width > 1 || height > 1) &&
      baseMipLevel + 1 < texture.mipLevelCount
    ) {
      width = Math.max(1, (width / 2) | 0);
      height = Math.max(1, (height / 2) | 0);

      const bindGroup = device.createBindGroup({
        layout: pipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: this.sampler! },
          {
            binding: 1,
            resource: texture.createView({
              dimension: '2d',
              baseMipLevel,
              mipLevelCount: 1,
              baseArrayLayer,
              arrayLayerCount: 1,
            }),
          },
        ],
      });

      ++baseMipLevel;

      const first = baseMipLevel === 1;
      const last = baseMipLevel === levels;
      const renderPassDescriptor: GPURenderPassDescriptor = {
        label: 'our basic canvas renderPass',
        timestampWrites:
          span && (first || last)
            ? ({
                querySet: span.querySet,
                beginningOfPassWriteIndex: first
                  ? span.beginningOfPassWriteIndex
                  : undefined,
                endOfPassWriteIndex: last
                  ? span.endOfPassWriteIndex
                  : undefined,
              } as unknown as GPURenderPassTimestampWrites)
            : undefined,
        colorAttachments: [
          {
            view: texture.createView({
              dimension: '2d',
              baseMipLevel,
              mipLevelCount: 1,
              baseArrayLayer,
              arrayLayerCount: 1,
            }),
            loadOp: 'clear',
            storeOp: 'store',
          },
        ],
      };

      const pass = encoder.beginRenderPass(renderPassDescriptor);
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      pass.draw(6); // call our vertex shader 6 times
      pass.end();
    }

    const commandBuffer = encoder.finish();
    device.queue.submit([commandBuffer]);
  }
}
